'use client'

import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useTranslation, useForm, zodResolver, z } from '@spcc/core'
import {
  FormDrawer,
  Input,
  Label,
  Switch,
  Badge,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  Button,
  Check,
  ChevronsUpDown,
  Spinner,
  X,
} from '@spcc/uikit'
import { MapPin, Globe, Building2, Hash, AlignLeft } from 'lucide-react'
import type { LoadingLocationRule, LoadingLocationRuleFormData } from '../../domain/types'
import { referenceDataApi } from '../../infrastructure'

const DEBOUNCE_MS = 400

const formSchema = z.object({
  loadingLocationId: z.string().min(1, 'Loading location is required'),
  cityIds: z.array(z.string()).default([]),
  tenantIds: z.array(z.string()).default([]),
  priority: z.preprocess(Number, z.number().min(0, 'Must be ≥ 0')),
  isActive: z.boolean().default(true),
  description: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

export interface LoadingLocationRuleFormDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  rule?: LoadingLocationRule | null
  loadingLocations: { documentId: string; label: string }[]
  cities: { documentId: string; name: string; nameEn?: string; nameAr?: string }[]
  customers: { documentId: string; name: string }[]
  onSubmit: (data: LoadingLocationRuleFormData) => void
  isLoading?: boolean
  error?: string | null
  onClearError?: () => void
  isRTL?: boolean
}

function FieldRow({
  label,
  icon: Icon,
  error,
  children,
  hint,
}: {
  label: string
  icon: React.ElementType
  error?: string
  children: React.ReactNode
  hint?: string
}) {
  return (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
        {label}
      </Label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </p>
      {children}
    </div>
  )
}

// ─── Combobox Item type ─────────────────────────────────────────────────────

interface ComboboxItem {
  value: string
  label: string
  labelSecondary?: string
}

// ─── Single-Select Combobox (required variant) ──────────────────────────────

function SearchableComboboxRequired({
  items,
  value,
  onSelect,
  placeholder,
  searchPlaceholder,
  notFoundText,
  onSearch,
  searching,
  isRTL,
}: {
  items: ComboboxItem[]
  value: string
  onSelect: (value: string) => void
  placeholder: string
  searchPlaceholder: string
  notFoundText: string
  onSearch?: (query: string) => void
  searching?: boolean
  isRTL?: boolean
}) {
  const [open, setOpen] = useState(false)
  const selectedItem = items.find((i) => i.value === value)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-10 font-normal"
        >
          <span className={cn('truncate', !selectedItem && 'text-muted-foreground')}>
            {selectedItem ? selectedItem.label : placeholder}
          </span>
          <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command shouldFilter={!onSearch}>
          <CommandInput placeholder={searchPlaceholder} onValueChange={onSearch} />
          <CommandList>
            {searching ? (
              <div className="flex items-center justify-center py-6">
                <Spinner className="h-5 w-5" />
              </div>
            ) : (
              <>
                <CommandEmpty>{notFoundText}</CommandEmpty>
                <CommandGroup>
                  {items.map((item) => (
                    <CommandItem
                      key={item.value}
                      value={item.label}
                      onSelect={() => { onSelect(item.value); setOpen(false) }}
                      className="group"
                    >
                      <Check className={cn('h-4 w-4 shrink-0', isRTL ? 'ml-2' : 'mr-2', value === item.value ? 'opacity-100' : 'opacity-0')} />
                      <span>{item.label}</span>
                      {item.labelSecondary && !isRTL && (
                        <span className="ml-auto text-xs text-muted-foreground group-data-[selected=true]:text-white">{item.labelSecondary}</span>
                      )}
                      {item.labelSecondary && isRTL && item.labelSecondary !== item.label && (
                        <span className="mr-auto text-xs text-muted-foreground group-data-[selected=true]:text-white">{item.labelSecondary}</span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// ─── Multi-Select Combobox ──────────────────────────────────────────────────

function SearchableMultiCombobox({
  items,
  values,
  onSelect,
  onRemove,
  placeholder,
  searchPlaceholder,
  notFoundText,
  onSearch,
  searching,
  isRTL,
}: {
  items: ComboboxItem[]
  values: string[]
  onSelect: (value: string) => void
  onRemove: (value: string) => void
  placeholder: string
  searchPlaceholder: string
  notFoundText: string
  onSearch?: (query: string) => void
  searching?: boolean
  isRTL?: boolean
}) {
  const [open, setOpen] = useState(false)

  const selectedItems = items.filter((i) => values.includes(i.value))

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-auto min-h-10 font-normal py-2"
        >
          <div className="flex flex-wrap gap-1 items-center flex-1">
            {selectedItems.length > 0 ? (
              selectedItems.map((item) => (
                <Badge
                  key={item.value}
                  variant="secondary"
                  className="text-xs px-1.5 py-0 gap-1 max-w-[140px]"
                >
                  <span className="truncate">{item.label}</span>
                  <button
                    type="button"
                    className="shrink-0 hover:text-destructive"
                    onClick={(e) => { e.stopPropagation(); onRemove(item.value) }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command shouldFilter={!onSearch}>
          <CommandInput placeholder={searchPlaceholder} onValueChange={onSearch} />
          <CommandList>
            {searching ? (
              <div className="flex items-center justify-center py-6">
                <Spinner className="h-5 w-5" />
              </div>
            ) : (
              <>
                <CommandEmpty>{notFoundText}</CommandEmpty>
                <CommandGroup>
                  {items.map((item) => {
                    const isSelected = values.includes(item.value)
                    return (
                      <CommandItem
                        key={item.value}
                        value={item.label}
                        onSelect={() => onSelect(item.value)}
                        className="group"
                      >
                        <div className={cn('h-4 w-4 shrink-0 border rounded flex items-center justify-center', isRTL ? 'ml-2' : 'mr-2', isSelected ? 'bg-primary border-primary' : 'border-input')}>
                          {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                        </div>
                        <span>{item.label}</span>
                        {item.labelSecondary && !isRTL && (
                          <span className="ml-auto text-xs text-muted-foreground group-data-[selected=true]:text-white">{item.labelSecondary}</span>
                        )}
                        {item.labelSecondary && isRTL && item.labelSecondary !== item.label && (
                          <span className="mr-auto text-xs text-muted-foreground group-data-[selected=true]:text-white">{item.labelSecondary}</span>
                        )}
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// ─── Debounce hook ──────────────────────────────────────────────────────────

function useDebouncedSearch(debounceMs = DEBOUNCE_MS) {
  const [input, setInput] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(input), debounceMs)
    return () => window.clearTimeout(id)
  }, [input, debounceMs])

  return { input, setInput, debounced }
}

// ─── Component ──────────────────────────────────────────────────────────────

export function LoadingLocationRuleFormDrawer({
  open,
  onOpenChange,
  rule,
  loadingLocations = [],
  cities = [],
  customers = [],
  onSubmit,
  isLoading = false,
  error,
  onClearError,
  isRTL = false,
}: LoadingLocationRuleFormDrawerProps) {
  const { t } = useTranslation()
  const isEditing = !!rule

  const defaultValues: FormValues = useMemo(
    () => ({
      loadingLocationId: rule?.loadingLocation?.documentId ?? '',
      cityIds: rule?.cities?.map((c) => c.documentId) ?? [],
      tenantIds: rule?.tenants?.map((t) => t.documentId) ?? [],
      priority: rule?.priority ?? 0,
      isActive: rule?.isActive ?? true,
      description: rule?.description ?? '',
    }),
    [rule]
  )

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema) as any,
    defaultValues,
  })

  useEffect(() => {
    if (open) reset(defaultValues)
  }, [open, defaultValues, reset])

  const watchedIsActive = watch('isActive')

  // ── Server-side search state ──────────────────────────────────────────────

  const llSearch = useDebouncedSearch()
  const citySearch = useDebouncedSearch()
  const customerSearch = useDebouncedSearch()

  const [llResults, setLlResults] = useState<ComboboxItem[] | null>(null)
  const [cityResults, setCityResults] = useState<ComboboxItem[] | null>(null)
  const [customerResults, setCustomerResults] = useState<ComboboxItem[] | null>(null)

  const [llSearching, setLlSearching] = useState(false)
  const [citySearching, setCitySearching] = useState(false)
  const [customerSearching, setCustomerSearching] = useState(false)

  const llFetchId = useRef(0)
  const cityFetchId = useRef(0)
  const customerFetchId = useRef(0)

  // Loading locations search
  useEffect(() => {
    const q = llSearch.debounced.trim()
    if (!q) { setLlResults(null); return }
    const id = ++llFetchId.current
    setLlSearching(true)
    referenceDataApi.searchLoadingLocations(q).then((data) => {
      if (id !== llFetchId.current) return
      setLlResults(data.map((d: any) => ({ value: d.documentId, label: d.label })))
      setLlSearching(false)
    }).catch(() => {
      if (id !== llFetchId.current) return
      setLlResults([])
      setLlSearching(false)
    })
  }, [llSearch.debounced])

  // Cities search — combine EN + AR names into one label
  useEffect(() => {
    const q = citySearch.debounced.trim()
    if (!q) { setCityResults(null); return }
    const id = ++cityFetchId.current
    setCitySearching(true)
    referenceDataApi.searchCities(q).then((data) => {
      if (id !== cityFetchId.current) return
      const mapped = data.map((d: any) => {
        let nameEn = d.name
        let nameAr: string | undefined

        const localizations = d.localizations?.data ?? d.localizations ?? []
        if (Array.isArray(localizations)) {
          for (const loc of localizations) {
            const attrs = loc?.attributes ?? loc
            if (attrs?.locale === 'en' && attrs?.name) nameEn = attrs.name
            if (attrs?.locale === 'ar' && attrs?.name) nameAr = attrs.name
          }
        }

        const label = isRTL ? (nameAr || nameEn) : (nameEn || nameAr)
        const labelSecondary = isRTL ? (nameEn || undefined) : (nameAr || undefined)

        return { value: d.documentId, label, labelSecondary }
      })
      setCityResults(mapped)
      setCitySearching(false)
    }).catch(() => {
      if (id !== cityFetchId.current) return
      setCityResults([])
      setCitySearching(false)
    })
  }, [citySearch.debounced, isRTL])

  // Customers search
  useEffect(() => {
    const q = customerSearch.debounced.trim()
    if (!q) { setCustomerResults(null); return }
    const id = ++customerFetchId.current
    setCustomerSearching(true)
    referenceDataApi.searchTenants(q).then((data) => {
      if (id !== customerFetchId.current) return
      setCustomerResults(data.map((d: any) => ({ value: d.documentId, label: d.code ? `${d.name} (${d.code})` : d.name })))
      setCustomerSearching(false)
    }).catch(() => {
      if (id !== customerFetchId.current) return
      setCustomerResults([])
      setCustomerSearching(false)
    })
  }, [customerSearch.debounced])

  // Handle multi-select toggle
  const toggleCity = useCallback((docId: string) => {
    const current = watch('cityIds')
    if (current.includes(docId)) {
      setValue('cityIds', current.filter((id) => id !== docId))
    } else {
      setValue('cityIds', [...current, docId])
    }
  }, [watch, setValue])

  const removeCity = useCallback((docId: string) => {
    setValue('cityIds', watch('cityIds').filter((id) => id !== docId))
  }, [watch, setValue])

  const toggleTenant = useCallback((docId: string) => {
    const current = watch('tenantIds')
    if (current.includes(docId)) {
      setValue('tenantIds', current.filter((id) => id !== docId))
    } else {
      setValue('tenantIds', [...current, docId])
    }
  }, [watch, setValue])

  const removeTenant = useCallback((docId: string) => {
    setValue('tenantIds', watch('tenantIds').filter((id) => id !== docId))
  }, [watch, setValue])

  // Reset searches when drawer closes
  useEffect(() => {
    if (!open) {
      llSearch.setInput('')
      citySearch.setInput('')
      customerSearch.setInput('')
      setLlResults(null)
      setCityResults(null)
      setCustomerResults(null)
      setLlSearching(false)
      setCitySearching(false)
      setCustomerSearching(false)
      llFetchId.current = 0
      cityFetchId.current = 0
      customerFetchId.current = 0
    }
  }, [open])

  const locationItems: ComboboxItem[] = useMemo(
    () => llResults ?? loadingLocations.map((ll) => ({ value: ll.documentId, label: ll.label })),
    [llResults, loadingLocations]
  )

  const cityItems: ComboboxItem[] = useMemo(
    () => cityResults ?? cities.map((c) => {
      const label = isRTL
        ? (c.nameAr || c.nameEn || c.name)
        : (c.nameEn || c.nameAr || c.name)
      const labelSecondary = isRTL
        ? (c.nameEn || undefined)
        : (c.nameAr || undefined)
      return { value: c.documentId, label, labelSecondary }
    }),
    [cityResults, cities, isRTL]
  )

  const customerItems: ComboboxItem[] = useMemo(
    () => customerResults ?? customers.map((c) => ({ value: c.documentId, label: c.name })),
    [customerResults, customers]
  )

  const headerContent = (
    <div className="space-y-1">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
          <MapPin className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-white leading-tight">
            {isEditing ? t('queue.editRule') : t('queue.addRule')}
          </h2>
          <p className="text-sm text-white/70">
            {isEditing ? rule?.loadingLocation?.label : t('queue.ruleForm.section')}
          </p>
        </div>
      </div>
    </div>
  )

  return (
    <FormDrawer
      key={rule?.documentId ?? 'new-rule'}
      open={open}
      onOpenChange={onOpenChange}
      headerContent={headerContent}
      headerGradient="from-indigo-600 to-purple-700"
      title={isEditing ? t('queue.editRule') : t('queue.addRule')}
      isLoading={isLoading}
      isSubmitting={isSubmitting}
      error={error}
      onClearError={onClearError}
      onSave={handleSubmit((data) =>
        onSubmit({
          loadingLocationId: data.loadingLocationId,
          cityIds: data.cityIds.length > 0 ? data.cityIds : undefined,
          tenantIds: data.tenantIds.length > 0 ? data.tenantIds : undefined,
          priority: data.priority,
          isActive: data.isActive,
          description: data.description || undefined,
        })
      )}
      saveText={isEditing ? t('common.save') : t('queue.addRule')}
      cancelText={t('common.cancel')}
      isRTL={isRTL}
      fixedWidth={480}
      modal={false}
    >
      <div className="space-y-6">

        <Section title={t('queue.ruleForm.section')}>
          <FieldRow
            label={t('queue.ruleForm.loadingLocation')}
            icon={MapPin}
            error={errors.loadingLocationId?.message}
          >
            <SearchableComboboxRequired
              items={locationItems}
              value={watch('loadingLocationId')}
              onSelect={(v) => setValue('loadingLocationId', v)}
              placeholder={t('queue.ruleForm.selectLoadingLocation')}
              searchPlaceholder={t('common.search')}
              notFoundText={t('common.noResults')}
              onSearch={llSearch.setInput}
              searching={llSearching}
              isRTL={isRTL}
            />
          </FieldRow>

          <FieldRow
            label={t('queue.ruleForm.cities')}
            icon={Globe}
            error={errors.cityIds?.message}
            hint={t('queue.ruleForm.anyCity')}
          >
            <SearchableMultiCombobox
              items={cityItems}
              values={watch('cityIds')}
              onSelect={toggleCity}
              onRemove={removeCity}
              placeholder={t('queue.ruleForm.selectCities')}
              searchPlaceholder={t('common.search')}
              notFoundText={t('common.noResults')}
              onSearch={citySearch.setInput}
              searching={citySearching}
              isRTL={isRTL}
            />
          </FieldRow>

          <FieldRow
            label={t('queue.ruleForm.customers')}
            icon={Building2}
            error={errors.tenantIds?.message}
            hint={t('queue.ruleForm.allCustomers')}
          >
            <SearchableMultiCombobox
              items={customerItems}
              values={watch('tenantIds')}
              onSelect={toggleTenant}
              onRemove={removeTenant}
              placeholder={t('queue.ruleForm.selectCustomers')}
              searchPlaceholder={t('common.search')}
              notFoundText={t('common.noResults')}
              onSearch={customerSearch.setInput}
              searching={customerSearching}
              isRTL={isRTL}
            />
          </FieldRow>

          <FieldRow
            label={t('queue.ruleForm.priority')}
            icon={Hash}
            error={errors.priority?.message}
          >
            <Input
              {...register('priority')}
              type="number"
              min={0}
              className="h-10"
              placeholder="0"
            />
          </FieldRow>

          <FieldRow
            label={t('queue.ruleForm.description')}
            icon={AlignLeft}
            error={errors.description?.message}
          >
            <Input
              {...register('description')}
              placeholder={t('queue.ruleForm.descriptionPlaceholder')}
              className="h-10"
            />
          </FieldRow>
        </Section>

        <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/30">
          <div className="space-y-0.5">
            <p className="text-sm font-medium">{t('queue.ruleForm.isActive')}</p>
            <p className="text-xs text-muted-foreground">{t('queue.ruleForm.isActiveHelp')}</p>
          </div>
          <Switch
            checked={watchedIsActive}
            onCheckedChange={(v) => setValue('isActive', v)}
          />
        </div>

      </div>
    </FormDrawer>
  )
}
